import React, { useState } from 'react';
import { Copy, Search, Loader2, Package } from 'lucide-react';
import { porderRegistrationService } from '../../../../services/porderRegistrationService';
import { pOrdersService } from '../../../../services/pOrdersService';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrdersDto } from '../../../../types/porders';
import { POrderLine } from '../../../../types/porderRegistration';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../common/StatusBadge';

interface CopyPOrderDialogProps {
  onSuccess: (newOrderNo: number) => void;
}

export const CopyPOrderDialog: React.FC<CopyPOrderDialogProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [searchOrderNo, setSearchOrderNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foundOrder, setFoundOrder] = useState<POrdersDto | null>(null);
  const [foundOrderLines, setFoundOrderLines] = useState<POrderLine[]>([]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    const orderNo = parseInt(searchOrderNo);
    if (isNaN(orderNo) || orderNo <= 0) {
      setError(t('manager.modules.porderRegistration.copy.invalidOrderNo', {}, 'Ugyldig ordrenummer'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFoundOrder(null);
      setFoundOrderLines([]);

      // Fetch purchase order
      const order = await pOrdersService.getOrderByOrderNo(orderNo);

      if (!order) {
        setError(t('manager.modules.porderRegistration.copy.orderNotFound', { '0': orderNo }, `Innkjøpsordre ${orderNo} ikke funnet`));
        return;
      }

      // Fetch purchase order lines
      const lines = await pOrderLineService.getOrderLines(orderNo);

      // Convert lines to POrderLine type
      const convertedLines: POrderLine[] = lines.map(line => ({
        id: line.id,
        companyNo: line.companyNo,
        orderNo: line.orderNo,
        lineNo: line.lineNo,
        lineNoERP: line.lineNoERP,
        lineStatus: line.lineStatus,
        itemNo: line.itemNo,
        itemBarCode: line.itemBarCode,
        description1: line.description1,
        itemGroup1: line.itemGroup1,
        itemGroup2: line.itemGroup2,
        itemGroup3: line.itemGroup3,
        quantity: line.quantity,
        quantityReceived: line.quantityDelivered,
        itemUnitCode: line.itemUnitCode,
        itemUnitTxt: line.itemUnitTxt,
        price: line.price,
        deliveryDate: line.deliveryDate ? new Date(line.deliveryDate).toISOString() : undefined,
        serialNoItemYesNo: line.serialNoItemYesNo,
        stdLocationName: line.stdLocationName,
        warehouse: line.warehouse,
        weight: undefined,
        height: undefined,
        length: undefined,
        width: undefined,
        volum: undefined,
        itemPictureName: undefined,
        updatedBy: line.updatedBy,
        lastUpdate: line.lastUpdate ? new Date(line.lastUpdate).toISOString() : undefined,
        createdBy: line.createdBy,
        created: line.created ? new Date(line.created).toISOString() : undefined,
        statusText: line.statusText || '',
        remainingQuantity: line.remainingQuantity
      }));

      setFoundOrder(order);
      setFoundOrderLines(convertedLines);
    } catch (err) {
      console.error('Error searching for purchase order:', err);
      setError(err instanceof Error ? err.message : t('manager.modules.porderRegistration.copy.searchFailed', {}, 'Kunne ikke finne innkjøpsordre'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!foundOrder) return;

    try {
      setCopying(true);
      setError(null);

      const response = await porderRegistrationService.copyPOrder(foundOrder.orderNo);

      if (response.success) {
        alert(t('manager.modules.porderRegistration.copy.copiedAlert', { '0': response.newOrderNo }, `Innkjøpsordre kopiert! Nytt ordrenummer: ${response.newOrderNo}`));
        onSuccess(response.newOrderNo);

        // Reset form
        setSearchOrderNo('');
        setFoundOrder(null);
        setFoundOrderLines([]);
      } else {
        throw new Error(response.message || t('manager.modules.porderRegistration.copy.copyFailed', {}, 'Kunne ikke kopiere innkjøpsordre'));
      }
    } catch (err) {
      console.error('Error copying purchase order:', err);
      setError(err instanceof Error ? err.message : t('manager.modules.porderRegistration.copy.copyFailedRetry', {}, 'Kunne ikke kopiere innkjøpsordre. Prøv igjen.'));
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('manager.modules.porderRegistration.copy.searchTitle', {}, 'Søk etter innkjøpsordre')}</h3>
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.porderRegistration.copy.orderNoLabel', {}, 'Ordrenummer')}
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={searchOrderNo}
                onChange={(e) => setSearchOrderNo(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('manager.modules.porderRegistration.copy.orderNoPlaceholder', {}, 'Skriv inn ordrenummer...')}
                min="1"
              />
              <button
                type="submit"
                disabled={loading || !searchOrderNo}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    {t('manager.modules.porderRegistration.copy.searching', {}, 'Søker...')}
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    {t('common.buttons.search', {}, 'Søk')}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        )}
      </div>

      {/* Order Details */}
      {foundOrder && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('manager.modules.porderRegistration.copy.orderFoundTitle', {}, 'Innkjøpsordre funnet')}</h3>
            <button
              onClick={handleCopy}
              disabled={copying}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors font-medium"
            >
              {copying ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t('manager.modules.porderRegistration.copy.copying', {}, 'Kopierer...')}
                </>
              ) : (
                <>
                  <Copy size={20} />
                  {t('manager.modules.porderRegistration.copy.copyButton', {}, 'Kopier Innkjøpsordre')}
                </>
              )}
            </button>
          </div>

          {/* Order Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <span className="text-sm font-medium text-gray-500">{t('manager.modules.porderRegistration.copy.labelOrderNo', {}, 'Ordrenummer:')}</span>
              <div className="text-base font-semibold text-gray-900">{foundOrder.orderNo}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('manager.modules.porderRegistration.copy.labelOrderNoErp', {}, 'Ordrenr ERP:')}</span>
              <div className="text-base text-gray-900">{foundOrder.orderNoERP}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('manager.modules.porderRegistration.copy.labelSupplierNo', {}, 'Leverandørnummer:')}</span>
              <div className="text-base text-gray-900">{foundOrder.supplierNo}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('manager.modules.porderRegistration.copy.labelSupplierName', {}, 'Leverandørnavn:')}</span>
              <div className="text-base text-gray-900">{foundOrder.supplierName || '-'}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('manager.modules.porderRegistration.copy.labelWarehouse', {}, 'Lager:')}</span>
              <div className="text-base text-gray-900">{foundOrder.warehouse || '-'}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('manager.modules.porderRegistration.copy.labelDeliveryDate', {}, 'Leveringsdato:')}</span>
              <div className="text-base text-gray-900">
                {porderRegistrationService.formatDate(foundOrder.deliveryDate?.toString())}
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t('manager.modules.porderRegistration.copy.labelStatus', {}, 'Status:')}</span>
              <div className="mt-0.5">
                <StatusBadge value={foundOrder.orderStatus} statusType={1} />
              </div>
            </div>
          </div>

          {/* Order Lines */}
          {foundOrderLines.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package size={16} />
                {t('manager.modules.porderRegistration.copy.orderLinesCount', { '0': foundOrderLines.length }, `Ordrelinjer (${foundOrderLines.length})`)}
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('manager.modules.porderRegistration.copy.colLine', {}, 'Linje')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('manager.modules.porderRegistration.create.colItemNo', {}, 'Varenr')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('common.fields.description', {}, 'Beskrivelse')}
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('common.fields.quantity', {}, 'Antall')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('manager.modules.porderRegistration.create.colUnit', {}, 'Enhet')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('warehouse.general.bin', {}, 'Lokasjon')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {foundOrderLines.map((line) => (
                      <tr key={line.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-900">{line.lineNo}</td>
                        <td className="px-3 py-2 text-sm font-mono text-gray-900">{line.itemNo}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{line.description1 || '-'}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900 font-medium">
                          {porderRegistrationService.formatQuantity(line.quantity)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">{line.itemUnitCode || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{line.stdLocationName || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      {!foundOrder && !error && !loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Search className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">{t('manager.modules.porderRegistration.copy.infoTitle', {}, 'Slik kopierer du en innkjøpsordre')}</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>{t('manager.modules.porderRegistration.copy.infoStep1', {}, 'Skriv inn ordrenummeret du ønsker å kopiere')}</li>
                <li>{t('manager.modules.porderRegistration.copy.infoStep2', {}, 'Klikk på "Søk" for å finne innkjøpsordren')}</li>
                <li>{t('manager.modules.porderRegistration.copy.infoStep3', {}, 'Gjennomgå ordreinformasjonen')}</li>
                <li>{t('manager.modules.porderRegistration.copy.infoStep4', {}, 'Klikk på "Kopier Innkjøpsordre" for å lage en kopi')}</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
